import {
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from "@fluentui/react-components";
import {
  CalendarLtr16Regular,
  CheckboxChecked16Regular,
  Diamond12Filled,
  Flag16Filled,
  Folder16Regular,
} from "@fluentui/react-icons";
import type { DragEvent } from "react";
import type { ISODate } from "@/engine/date";
import type { CalendarEvent, Segment } from "@/features/calendar-view/calendar-layout";
import { formatDate, formatDateRange } from "@/lib/date-format";

const useStyles = makeStyles({
  seg: {
    position: "absolute",
    pointerEvents: "auto",
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "0 6px",
    fontSize: "11px",
    lineHeight: "20px",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    borderRadius: tokens.borderRadiusSmall,
    cursor: "pointer",
    boxSizing: "border-box",
    userSelect: "none",
    transition: "filter 0.15s ease, opacity 0.15s ease",
    ":hover": {
      filter: "var(--cal-hover)",
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  segTitle: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
    flex: 1,
  },
  segMeta: {
    fontSize: "10px",
    opacity: 0.8,
    flexShrink: 0,
    marginLeft: "2px",
  },
  contLeft: {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
  contRight: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  arrowHint: {
    fontSize: "9px",
    opacity: 0.7,
    flexShrink: 0,
    userSelect: "none",
  },
  /* 종류별 스타일 */
  wbs: {
    backgroundColor: "var(--cal-wbs-bg)",
    color: "var(--cal-wbs-fg)",
    borderTop: "1px solid var(--cal-wbs-line)",
    borderRight: "1px solid var(--cal-wbs-line)",
    borderBottom: "1px solid var(--cal-wbs-line)",
    borderLeft: "3px solid var(--cal-wbs-edge)",
    fontWeight: tokens.fontWeightRegular,
  },
  wbsContLeft: {
    borderLeft: "1px solid var(--cal-wbs-line)",
  },
  activity: {
    backgroundColor: "var(--cal-act-bg)",
    color: "var(--cal-act-fg)",
    borderTop: "1px solid var(--cal-act-line)",
    borderRight: "1px solid var(--cal-act-line)",
    borderBottom: "1px solid var(--cal-act-line)",
    borderLeft: "3px solid var(--cal-act-edge)",
    fontWeight: tokens.fontWeightRegular,
  },
  activityContLeft: {
    borderLeft: "1px solid var(--cal-act-line)",
  },
  critical: {
    backgroundColor: "var(--cal-crit-bg)",
    color: "var(--cal-crit-fg)",
    borderTop: "1px solid var(--cal-crit-line)",
    borderRight: "1px solid var(--cal-crit-line)",
    borderBottom: "1px solid var(--cal-crit-line)",
    borderLeft: "3px solid var(--cal-crit-edge)",
  },
  criticalContLeft: {
    borderLeft: "1px solid var(--cal-crit-line)",
  },
  execution: {
    backgroundColor: "var(--cal-task-bg)",
    color: "var(--cal-task-fg)",
    borderTop: "1px solid var(--cal-task-line)",
    borderRight: "1px solid var(--cal-task-line)",
    borderBottom: "1px solid var(--cal-task-line)",
    borderLeft: "3px solid var(--cal-task-edge)",
    fontWeight: tokens.fontWeightRegular,
    cursor: "grab",
    ":active": {
      cursor: "grabbing",
    },
  },
  executionContLeft: {
    borderLeft: "1px solid var(--cal-task-line)",
  },
  executionOverdue: {
    backgroundColor: "var(--cal-late-bg)",
    color: "var(--cal-late-fg)",
    borderTop: "1px solid var(--cal-late-line)",
    borderRight: "1px solid var(--cal-late-line)",
    borderBottom: "1px solid var(--cal-late-line)",
    borderLeft: "3px solid var(--cal-late-edge)",
  },
  executionOverdueContLeft: {
    borderLeft: "1px solid var(--cal-late-line)",
  },
  executionPartial: {
    borderTopStyle: "dashed",
    borderRightStyle: "dashed",
    borderBottomStyle: "dashed",
    borderLeftStyle: "dashed",
  },
  milestone: {
    backgroundColor: "var(--cal-mile-bg)",
    color: "var(--cal-mile-fg)",
    borderTop: "1px solid var(--cal-mile-line)",
    borderRight: "1px solid var(--cal-mile-line)",
    borderBottom: "1px solid var(--cal-mile-line)",
    borderLeft: "3px solid var(--cal-mile-edge)",
    fontWeight: tokens.fontWeightRegular,
  },
  deadline: {
    backgroundColor: "var(--cal-due-bg)",
    color: "var(--cal-due-fg)",
    borderTop: "1px solid var(--cal-due-line)",
    borderRight: "1px solid var(--cal-due-line)",
    borderBottom: "1px solid var(--cal-due-line)",
    borderLeft: "3px solid var(--cal-due-edge)",
    fontWeight: tokens.fontWeightRegular,
  },
  done: {
    textDecoration: "line-through",
    opacity: 0.6,
  },
  dragging: {
    opacity: 0.35,
    pointerEvents: "none",
  },
});

export interface EventSegmentProps {
  seg: Segment;
  laneH: number;
  laneGap: number;
  topOffset: number;
  dragging?: boolean;
  onDragStart?: (e: DragEvent) => void;
  onDragEnd?: () => void;
  onOpen: (e: CalendarEvent) => void;
  today: ISODate;
}

export function EventSegment({
  seg,
  laneH,
  laneGap,
  topOffset,
  dragging = false,
  onDragStart,
  onDragEnd,
  onOpen,
  today,
}: EventSegmentProps) {
  const styles = useStyles();
  const e = seg.event;

  const isExecution = e.kind === "execution";
  const isOverdue = isExecution && !!e.overdue;
  const isCritical = e.kind === "activity" && !!e.critical;

  const cls = mergeClasses(
    styles.seg,
    e.kind === "wbs" && styles.wbs,
    e.kind === "wbs" && seg.continuesLeft && styles.wbsContLeft,
    e.kind === "activity" && !isCritical && styles.activity,
    e.kind === "activity" && !isCritical && seg.continuesLeft && styles.activityContLeft,
    isCritical && styles.critical,
    isCritical && seg.continuesLeft && styles.criticalContLeft,
    e.kind === "milestone" && styles.milestone,
    e.kind === "deadline" && styles.deadline,
    isExecution && !isOverdue && styles.execution,
    isExecution && !isOverdue && seg.continuesLeft && styles.executionContLeft,
    isOverdue && styles.executionOverdue,
    isOverdue && seg.continuesLeft && styles.executionOverdueContLeft,
    isExecution && e.partial && styles.executionPartial,
    e.done && styles.done,
    seg.continuesLeft && styles.contLeft,
    seg.continuesRight && styles.contRight,
    dragging && styles.dragging,
  );

  const kindLabel =
    e.kind === "execution"
      ? "Task"
      : e.kind === "wbs"
        ? "WBS"
        : e.kind === "milestone"
          ? "마일스톤"
          : e.kind === "deadline"
            ? "마감 제약"
            : "계획 작업";

  const dateLabel = formatDateRange(e.start, e.end, "short");

  const tip = `${kindLabel}${e.wbs && e.kind !== "execution" ? ` (${e.wbs})` : ""} · ${e.title} · ${dateLabel}${
    e.overdue ? ` · 지연 (오늘 ${formatDate(today, "short")})` : ""
  }`;

  const renderIcon = () => {
    switch (e.kind) {
      case "wbs":
        return <Folder16Regular style={{ fontSize: 12, flexShrink: 0 }} />;
      case "activity":
        return <CalendarLtr16Regular style={{ fontSize: 12, flexShrink: 0 }} />;
      case "execution":
        return <CheckboxChecked16Regular style={{ fontSize: 12, flexShrink: 0 }} />;
      case "milestone":
        return <Diamond12Filled style={{ fontSize: 11, flexShrink: 0 }} />;
      case "deadline":
        return <Flag16Filled style={{ fontSize: 11, flexShrink: 0 }} />;
      default:
        return null;
    }
  };

  const metaText = e.assignees?.[0] ?? "";

  return (
    <Tooltip content={tip} relationship="description" withArrow>
      <div
        className={cls}
        role="button"
        tabIndex={0}
        aria-label={tip}
        draggable={isExecution && !!onDragStart}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onClick={() => onOpen(e)}
        onKeyDown={(k) => {
          if (k.key === "Enter" || k.key === " ") {
            k.preventDefault();
            onOpen(e);
          }
        }}
        style={{
          left: `calc(${(seg.col / 7) * 100}% + 2px)`,
          width: `calc(${(seg.span / 7) * 100}% - 4px)`,
          top: topOffset + seg.lane * (laneH + laneGap),
          height: laneH,
        }}
      >
        {seg.continuesLeft && <span className={styles.arrowHint}>◀</span>}
        {renderIcon()}
        <span className={styles.segTitle}>
          {e.wbs && e.kind !== "execution" ? `${e.wbs} ` : ""}
          {e.title}
        </span>
        {metaText && <span className={styles.segMeta}>{metaText}</span>}
        {seg.continuesRight && <span className={styles.arrowHint}>▶</span>}
      </div>
    </Tooltip>
  );
}
